import { NavLink } from 'react-router'

import { Icon, type IconName } from './Icon'

/**
 * Bottom navigation — 33 screens (design-spec.md §5.2).
 *
 * 80 high, radius 16, `white/4%` on a `white/8%` border, with an 80px circle
 * cut from its top edge for the elevated centre action. The geometry here is
 * read from the dashboard node tree (`202:4113`), which is more reliable than
 * §5.2's prose: that paragraph lists five items "right-to-left" starting with
 * خانه, but خانه is the centre button, and the real right-to-left order is
 * رمز ارز ها · کیف پول · [خانه] · معامله · گزارشات.
 *
 * No render exists for any screen carrying this bar — the dashboard export is
 * only 148px wide — so it is built from geometry alone and belongs to the
 * 34-of-52 unverified set in design-spec.md §8.5.
 */
interface NavItem {
  to: string
  label: string
  icon: IconName
}

/** Source order is right-to-left, which is the reading order in RTL. */
const RIGHT_GROUP: NavItem[] = [
  { to: '/market', label: 'رمز ارز ها', icon: 'coins' },
  { to: '/wallet', label: 'کیف پول', icon: 'wallet' },
]

const LEFT_GROUP: NavItem[] = [
  { to: '/trade', label: 'معامله', icon: 'swap' },
  { to: '/reports', label: 'گزارشات', icon: 'chart' },
]

function NavItemLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      /* Pinned to the top of the 52px box rather than centred: the tree puts
         the icon at y 15..39 and the label at 43, and centring redistributes
         the 5px our 1.2 line-height saves over Figma's 22px text box, which
         pushed the whole item 2.6px down. */
      className="flex h-[52px] w-[56px] flex-col items-center gap-1 pt-px"
    >
      {({ isActive }) => (
        <>
          <Icon name={item.icon} size={24} className={isActive ? 'text-primary' : 'text-ink-50'} />
          <span
            className={`sx-tight text-14 font-semibold ${
              isActive ? 'text-primary' : 'text-ink-50'
            }`}
          >
            {item.label}
          </span>
        </>
      )}
    </NavLink>
  )
}

export function BottomNav() {
  return (
    <nav
      className="sticky bottom-0 z-(--sx-z-nav) w-full pt-[20px]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="relative h-nav w-full">
        <div
          /* The bar is only white/4%; on the 2447px dashboard the page
             scrolls underneath it, so it also needs a blur to stay readable. */
          className="sx-nav-bar absolute inset-0 rounded-16 border border-line-8 bg-surface-4 backdrop-blur-xl"
          aria-hidden="true"
        />

        {/* RTL puts the first child on the right, so the right-hand pair is
            written first. Groups are named for where they land on screen. */}
        <div className="relative flex h-full items-start justify-between px-4 pt-[14px]">
          <div className="flex gap-4">
            {RIGHT_GROUP.map((item) => (
              <NavItemLink key={item.to} item={item} />
            ))}
          </div>
          <div className="flex gap-4">
            {LEFT_GROUP.map((item) => (
              <NavItemLink key={item.to} item={item} />
            ))}
          </div>
        </div>

        {/* The centre action is elevated 20px into the notch: the FAB spans
            y -20..28 and its label y 48..70, so the gap under the FAB is 20 —
            not the 4 the side items use. That puts «خانه» 5px *below* the four
            side labels (43..65), which is what screens 18 and 44 both draw. */}
        <NavLink
          to="/"
          className="absolute -top-[20px] start-1/2 flex w-[56px] -translate-x-1/2 flex-col items-center gap-[20px] rtl:translate-x-1/2"
        >
          {({ isActive }) => (
            <>
              <span
                className={`flex size-[48px] items-center justify-center rounded-full ${
                  isActive ? 'bg-primary' : 'bg-primary/80'
                }`}
              >
                <Icon name="home" size={24} className="text-ink" />
              </span>
              <span
                className={`sx-tight text-14 font-semibold ${isActive ? 'text-primary' : 'text-ink'}`}
              >
                خانه
              </span>
            </>
          )}
        </NavLink>
      </div>
    </nav>
  )
}
